import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import toaster from 'utils/toaster';
import { getCanAccessFeature } from '../selectors/navigation';
import Button from '../components/atoms/Button';
import SingularTooltip from '../components/molecules/Tooltip';
import Icon, { IconSizes, IconTypes, IconVariants } from '../components/foundations/Icon';
import { runAssistCrossoverExport } from '../exportLogs/service';
import { getTaskLink } from '../exportLogs/utils';
import {
    ASSIST_CROSSOVER_API_TYPE,
    ASSIST_CROSSOVER_LOG_TYPE,
    ASSIST_CROSSOVER_DISABLED_REASONS,
    ASSIST_CROSSOVER_NO_APP_SITE_ERROR,
    buildAssistCrossoverFilters,
    getAssistCrossoverDisabledReasons,
} from './assistCrossover';
import { trackDashboardsMixpanelEvent } from './utils';
import css from './dashboard.css';

// Translation key for each reason the export can't run (shown in the tooltip and warning banner).
const DISABLED_MESSAGE_KEYS = {
    [ASSIST_CROSSOVER_DISABLED_REASONS.APP_OS]: 'STATIC.PAGES.ASSIST.CROSSOVER.DISABLED_TOOLTIP',
    [ASSIST_CROSSOVER_DISABLED_REASONS.DATE_RANGE]: 'STATIC.PAGES.ASSIST.CROSSOVER.DATE_RANGE_TOOLTIP',
};

// "Deep Dive Analysis" banner shown on the Assist & MTA page. Opens a download tab (like Export
// Logs) that generates a flattened assist crossover CSV using the page's current filters + dates.
// Enabled only for a single app + single platform, since the export maps onto one AppSite.
function AssistCrossoverBanner({ filters, startDate, endDate, widgetsReady = true, translate }) {
    const [loading, setLoading] = useState(false);
    // The crossover export view chain requires the EXPORT_LOGS app feature (and the 'assist' export
    // type); without it the click would open a blank tab and 500. Only render for orgs that have it.
    const canAccessExportLogs = useSelector(getCanAccessFeature('export_logs'));
    const disabledReasons = getAssistCrossoverDisabledReasons(filters, startDate);
    const canRun = disabledReasons.length === 0;
    // While the dashboard widgets are still loading they saturate the browser's per-host connection
    // pool, so the export request would stall behind them. Wait until they finish before enabling.
    const isPreparing = !widgetsReady;
    const isEnabled = canRun && !loading && !isPreparing;

    if (!canAccessExportLogs) {
        return null;
    }

    // The tooltip content is rendered by react-tippy into a detached popper outside the Localize
    // provider, so <Translate> there falls back to "Missing translationId". Resolve the strings up
    // front with translate() instead.
    const renderTooltipMessages = () =>
        disabledReasons.map(reason => <div key={reason}>{translate(DISABLED_MESSAGE_KEYS[reason])}</div>);
    const getTooltipContent = () => {
        if (isPreparing) {
            return translate('STATIC.PAGES.ASSIST.CROSSOVER.LOADING_TOOLTIP');
        }
        if (!canRun) {
            return renderTooltipMessages();
        }
        return null;
    };

    const runCrossoverExport = async downloadWindow => {
        // The backend resolves the app-site + partners from the filters and queues the export.
        const response = await runAssistCrossoverExport(buildAssistCrossoverFilters(filters, startDate, endDate));
        const taskId = response?.status === 0 ? response.value?.task_id : null;

        if (taskId) {
            downloadWindow.location.href = getTaskLink({
                taskId,
                logType: ASSIST_CROSSOVER_LOG_TYPE,
                apiType: ASSIST_CROSSOVER_API_TYPE,
            });
            downloadWindow.focus();
        } else {
            // RESOLVE_ERROR ("couldn't match the selected app + OS") only applies when the backend
            // reports no integrated app-site; any other queue failure gets the generic run error.
            const noAppSite = response?.value?.error === ASSIST_CROSSOVER_NO_APP_SITE_ERROR;
            toaster.error(
                translate(
                    noAppSite
                        ? 'STATIC.PAGES.ASSIST.CROSSOVER.RESOLVE_ERROR'
                        : 'STATIC.PAGES.ASSIST.CROSSOVER.RUN_ERROR'
                )
            );
            downloadWindow.close();
        }
    };

    const handleClick = () => {
        if (!isEnabled) {
            return;
        }
        trackDashboardsMixpanelEvent('Assist Crossover Export', {
            app: filters.app?.[0]?.label,
            platform: filters.os?.[0]?.label,
            source_count: filters.source?.length || 0,
            country_count: filters.country_field?.length || 0,
            start_date: startDate,
            end_date: endDate,
        });
        // Open the tab synchronously so it is not blocked as a popup, then fill it once ready.
        const downloadWindow = window.open('about:blank', '_blank');
        if (!downloadWindow) {
            toaster.error(translate('STATIC.PAGES.ASSIST.CROSSOVER.POPUP_ERROR'));
            return;
        }

        setLoading(true);
        runCrossoverExport(downloadWindow)
            .catch(() => {
                toaster.error(translate('STATIC.PAGES.ASSIST.CROSSOVER.RUN_ERROR'));
                downloadWindow.close();
            })
            .finally(() => setLoading(false));
    };

    return (
        <div className={css.crossoverBannerContainer}>
            <div className={css.crossoverBanner}>
                <div className={css.crossoverBannerMain}>
                    <div className={css.crossoverBannerInfo}>
                        <div className={css.crossoverBannerIconBadge}>
                            <Icon
                                name="file-analytics"
                                variant={IconVariants.ACTIVE}
                                size={IconSizes.LARGE}
                                interactive={false}
                            />
                        </div>
                        <div className={css.crossoverBannerText}>
                            <span className={css.crossoverBannerTitle}>
                                <Translate id="STATIC.PAGES.ASSIST.CROSSOVER.TITLE" />
                            </span>
                            <span className={css.crossoverBannerSubtitle}>
                                <Translate id="STATIC.PAGES.ASSIST.CROSSOVER.SUBTITLE" />
                            </span>
                        </div>
                    </div>
                    <SingularTooltip title={getTooltipContent()} position="top">
                        <Button
                            type="link"
                            disabled={!isEnabled}
                            onClick={handleClick}
                            textClassName={isEnabled ? css.crossoverButtonContent : css.crossoverButtonContentDisabled}
                        >
                            <Icon
                                name="file-download"
                                type={IconTypes.TABLER}
                                variant={isEnabled ? IconVariants.ACTIVE : IconVariants.LIGHT}
                                size={IconSizes.SMALL}
                                interactive={false}
                            />
                            <Translate id="STATIC.PAGES.ASSIST.CROSSOVER.BUTTON" />
                            {isPreparing && (
                                <Icon
                                    name="loader-2"
                                    type={IconTypes.TABLER}
                                    variant={IconVariants.LIGHT}
                                    size={IconSizes.SMALL}
                                    interactive={false}
                                    className={css.crossoverButtonSpinner}
                                />
                            )}
                        </Button>
                    </SingularTooltip>
                </div>
                <div className={css.crossoverBannerInfoText}>
                    <Translate id="STATIC.PAGES.ASSIST.CROSSOVER.INFO" />
                </div>
            </div>
        </div>
    );
}

AssistCrossoverBanner.propTypes = {
    filters: PropTypes.shape({
        app: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
        source: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
        os: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
        country_field: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
    }).isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    widgetsReady: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(AssistCrossoverBanner);
